/**
 * @module shared/payment-cards
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:max-file-line-count */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want

// Core Libraries
import { EventEmitter, Injectable } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { SelectItem } from 'primeng/api';

// Api endpoints
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';

// Interfaces
import { APIError } from '@support/api/common/error.interface';
import {
  PaymentMethodsData,
  StoredPayments,
} from '@support/api/proxies/responses/api-144.interface';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { CardLogo } from '@support/api/proxies/responses/api-528.interface';
import {
  ErrorMessages,
  PaymentCardInterface,
  PaymentFormInterface,
} from '../interfaces/payment-cards.interface';
import { PaymentMethods } from '@support/api/proxies/responses/api-419.interface';
import { StoredPaymentMethodsContentInterface } from '@experience/modules/manage-profile/billing-and-payment/interfaces/stored-payment-methods.interface';
import { Cart } from '@support/api/proxies/responses/api-cart-response.interface';

// Services
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

// Constants
import { BILLING_PAYMENT_CONSTANTS } from '@experience/modules/manage-profile/billing-and-payment/constants/billing-and-payment.constant';
import { SAVED_PAYMENT_CARD_CONSTANTS } from '@support/payment-cards/constants/payment-cards.constants';

import { SETTINGS } from '@environment/settings';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { CardTypeValidation } from '@support/api/proxies/responses/api-2611.interface';
import { Api2613Response } from '@support/api/proxies/responses/api-2613.interface';
import { PostNegativeFile } from '@support/payment-cards/models/order-payment-negative-file.model';
import { Api2614Response } from '@support/api/proxies/responses/api-2614.interface';
import { PostValidationModel } from '@support/payment-cards/models/order-payment-validation.model';
import { OrderPaymentModel } from '@experience/shop/order-payment/online-order-payment/models/online-order-payment.model';
import { PaymentInformation } from '@support/payment/online-payment/models/card-entry-form.model';
import { throwError as observableThrowError, Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { StoredPaymentsModel } from '@experience/modules/manage-profile/billing-and-payment/model/stored-payment.model';
import { ONLINE_PAYMENT } from '@support/payment/online-payment/constants/online-payment.constants';
import { randomNumber } from '@support/shared/services/random-number.helper';
const RADIX_VALUE_TEN = 10;
@Injectable()
export class PaymentCardsService {
  /**
   * Stores the error details for Card validation
   */
  // @TODO: will remove any with interface
  // tslint:disable-next-line:no-any
  public errorStatus: any;

  /**
   * Stores the data of selected payment method
   */
  public selectedStoredPayment: PaymentCardInterface;
  /**
   * Emit the negative file details
   */
  public negativeFileCheck: EventEmitter<string>;
  /**
   * Cart data to be displayed
   */
  public cartData: Cart;
  /**
   * Stores payment Methods
   */
  public storedPayments: PaymentMethodsData[];
  /**
   * Stores payment modal Data
   */
  public storedPaymentsModalData: StoredPaymentsModel;
  /**
   * Used to set paymentCardAlias in autopay update request
   */
  public paymentCardAliasAutoPay: string;
  /**  Un-subscriber for all subscriptions */
  private ngUnsubscribe: Subject<void>;
  private vestaToken: string;
  constructor(
    private apiGatewayService: ApiGatewayService,
    private stateStoreUtils: StateStoreUtilsService,
    private contentProviderService: ContentProviderService,
    private formBuilder: UntypedFormBuilder,
    private objectService: ObjectService,
    private formCreationService: FormCreationService,
    private commonApiService: CommonApiService
  ) {
    this.negativeFileCheck = new EventEmitter();
    this.ngUnsubscribe = new Subject<void>();
  }

  /**
   * Function to fetch the stored payment options
   * @returns Observable
   */
  public getStoredPaymentMethods(isAutoPayFlow?: boolean): Observable<StoredPayments> {
    const accountId = this.stateStoreUtils.getAccountId();
    const payload = {
      customerId: this.setCustomerID(),
      commonCustomerId: this.getCommonCustomerId(),
      accountId: accountId,
      paymentMethods: isAutoPayFlow
        ? BILLING_PAYMENT_CONSTANTS.AUTOPAY
        : BILLING_PAYMENT_CONSTANTS.PAYMENT_METHOD_NAME,
    };
    const apiToCall = API_MAP.API_2617_getStoredPaymentMethods;
    return this.apiGatewayService.postRequest(apiToCall, payload, null, null, null).pipe(
      map((response: HttpResponse<StoredPayments>) => {
        return response.body;
      }),
      catchError((error: APIError) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * Method for getting the AEM Contents of the Authorable data
   * @returns Observable
   */
  public getContentData(): Observable<object> {
    return this.contentProviderService
      .getContentData(CONTENT_ENDPOINT.STORED_PAYMENT_METHODS, 'shared')
      .pipe(
        map((contentData: object) => {
          return this.contentProviderService.getConfigurableData(contentData);
        })
      );
  }

  /**
   * Returns parent form group for SavedPaymentMethod form
   * @returns FormGroup
   */
  public buildSavedPaymentMethodForm(
    selectedStoredPayment: PaymentCardInterface,
    isCartPayment?: boolean
  ): UntypedFormGroup {
    const fieldValidationConstants = this.getFieldValidationConstants();
    const fullName = this.objectService.get(selectedStoredPayment, 'fullName')
      ? selectedStoredPayment.fullName
      : '';
    const cardNumber = this.objectService.get(selectedStoredPayment, 'cardNumber')
      ? selectedStoredPayment.cardNumber
      : '';
    const expirationMonthName = selectedStoredPayment
      ? this.generateExpirationMonthName(selectedStoredPayment)
      : '';
    const expirationYear = selectedStoredPayment ? selectedStoredPayment.year : '';
    let zipCode = '';
    if (!isCartPayment) {
      zipCode = this.objectService.get(selectedStoredPayment, 'zipCode')
        ? selectedStoredPayment.zipCode
        : '';
    }
    const createFormControl: PaymentFormInterface = {
      fullName: this.formCreationService.getFormControl(
        fieldValidationConstants.FULL_NAME,
        fullName,
        true
      ),
      cardNumber: this.formCreationService.getFormControl(
        fieldValidationConstants.CREDIT_CARD_NUMBER,
        cardNumber,
        true
      ),
      expirationMonthName: [expirationMonthName, Validators.required],
      expirationYear: [expirationYear, Validators.required],
      cvv: this.formCreationService.getFormControl(fieldValidationConstants.CVV, '', true),
    };
    if (!isCartPayment) {
      createFormControl.zipCode = this.formCreationService.getFormControl(
        fieldValidationConstants.ZIP_CODE,
        zipCode,
        true
      );
    }
    return this.formBuilder.group(createFormControl);
  }

  /**
   * Retrieves the fieldValidations constants from common API service
   * @returns ObjectInterface
   */
  public getFieldValidationConstants(): ObjectInterface {
    return this.commonApiService.getFieldValidationConstants();
  }
  // TODO: This can be used for WSIL1 Integration so not removing as of now
  // /**
  //  * Validates the card info by calling the API 487
  //  * @returns Observable<object>
  //  */
  // public validateCardInfo(vestaToken: string, paymentDetails?: PaymentCardModel): Observable<object> {
  //     const validateCardPayload = {
  //         tempToken: vestaToken,
  //         chargeAccountNumberIndicator: 'TemporaryToken',
  //         terminalType: 'POS'
  //     };
  //     return this.apiGatewayService.postRequest(API_MAP.API_487_postValidatePaymentMethod, validateCardPayload,
  //         null, null, null)
  //         .map((response: Response) => {
  //             const cardInfo: ValidatePaymentResponse = response.json();
  //             if (cardInfo.status === 'BLOCKED') {
  //               this.errorNegativeCheck({code: '140'}, paymentDetails);
  //             }
  //             return cardInfo;
  //         })
  //         .catch((errors: APIError) => {
  //             this.errorNegativeCheck(errors, paymentDetails);
  //             return observableThrowError(errors);
  //         });
  // }

  /**
   * Calls API 2613 for Cart payment page
   * @returns Observable<Api2613Response>
   */
  public validateCardInfo(encryptedCardNumber: string): Observable<Api2613Response> {
    const requestPayload = new PostNegativeFile(encryptedCardNumber);
    return this.apiGatewayService
      .postRequest(API_MAP.API_2613_postNegativeFile, requestPayload, null, null)
      .pipe(
        map((response: HttpResponse<Api2613Response>) => {
          return response.body;
        })
      );
  }

  /**
   * Calls API 2614 for Cart payment page
   * @returns Observable<Api2614Response>
   */
  public postValidation(paymentDetails: OrderPaymentModel): Observable<Api2614Response> {
    const requestPayload = new PostValidationModel(
      paymentDetails,
      null,
      this.stateStoreUtils.getAccountId()
    );
    return this.apiGatewayService
      .postRequest(API_MAP.API_2614_postValidation, requestPayload, null, null)
      .pipe(
        map((response: HttpResponse<Api2614Response>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Validates the card info by calling the API 487
   * @returns Observable<object>
   */
  public errorNegativeCheck(errors: APIError, paymentDetails?: PaymentInformation): void {
    this.handlePaymentErrors(errors.code, paymentDetails);
  }

  /**
   * Function to fetch all the eligible payment methods
   * @returns Observable<object>
   */
  public fetchEligiblePaymentMethods(): Observable<PaymentMethods[]> {
    this.cartData = this.stateStoreUtils.getCart();
    const accountType = this.stateStoreUtils.getAccountType();
    const payload = {
      transactionType: this.objectService.get(this.cartData, 'orderType')
        ? this.objectService.get(this.cartData, 'orderType')
        : BILLING_PAYMENT_CONSTANTS.TRANSACTION_TYPE,
      channel: SETTINGS.CHANNEL,
      accountType: accountType || ONLINE_PAYMENT.INDIVIDUAL,
      customerTypeId: ONLINE_PAYMENT.PREPAID,
      customerId: this.setCustomerID(),
      commonCustomerId: this.getCommonCustomerId(),
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_419_getEligiblePaymentMethods, payload, null, null, null)
      .pipe(
        map((response: HttpResponse<PaymentMethods[]>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * set customer ID for fetch eligible payments
   */
  private setCustomerID(): string {
    return this.stateStoreUtils.getCustomerId() &&
      this.stateStoreUtils.getCustomerId() !== SAVED_PAYMENT_CARD_CONSTANTS.ANON
      ? this.stateStoreUtils.getCustomerId()
      : null;
  }
  /**
   * used to get common customer id
   * @returns {string}
   */
  public getCommonCustomerId(): string {
    // if (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB) {
    const ccid = this.objectService.get(
      this.stateStoreUtils.getAccountLinesInfo(),
      'customer.ccid'
    );
    return ccid && this.stateStoreUtils.getCustomerId() !== SAVED_PAYMENT_CARD_CONSTANTS.ANON
      ? ccid
      : null;
    // } else {
    //   return (this.stateStoreUtils.getCustomerId() === SAVED_PAYMENT_CARD_CONSTANTS.ANON
    //     ? null : this.stateStoreUtils.getCustomerId());
    // }
  }

  /**
   * Retrives the Card Info from the API 528
   * @returns Observable<CardLogo>
   */
  public getCardLogo(cardNumber: string): Observable<CardLogo> {
    const payload = {
      bin: cardNumber,
    };
    const apiToCall = API_MAP.API_2611_cardTypeValidation;
    return this.apiGatewayService.postRequest(apiToCall, payload, null, null, null).pipe(
      map((response: HttpResponse<CardLogo>) => {
        return response.body;
      }),
      catchError((error: APIError) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * This is used to generate expiration month name of the card
   * @returns string value of month name
   */
  private generateExpirationMonthName(selectedStoredPayment: PaymentCardInterface): number {
    if (selectedStoredPayment) {
      const expirationMonth = parseInt(selectedStoredPayment.month, RADIX_VALUE_TEN);
      return expirationMonth;
    }
  }
  /**
   * Function to get year options for drop down
   * @returns void
   */
  public getYearOptions(): SelectItem[] {
    const currentYear: number = new Date().getFullYear();
    const yearOptions: SelectItem[] = [];
    const totalYears = 21;
    for (let i = 0; i < totalYears; i++) {
      const year: number = currentYear + i;
      yearOptions.push({ label: year.toString(), value: year });
    }
    return yearOptions;
  }

  /**
   * Fetches the modal content
   * @return removeModal
   */
  public getStaticModalContent(aemContentData: StoredPaymentMethodsContentInterface): StaticModal {
    const removeModal: StaticModal = {
      headerText: aemContentData.removeStoredPaymentMethod,
      bodyText: aemContentData.removePaymentMethodConfirmationMessage,
      disableDismiss: false,
      disableEsc: true,
      buttons: [
        {
          id: 'button1',
          type: 'submit',
          name: aemContentData.continue,
          class: 'btn btn-primary',
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
    };
    return removeModal;
  }

  /**
   * Error Handler is used to collect all the uiMessages to show
   * @param errorMessages
   * @param {PaymentCardModel} paymentDetails
   * @returns {Object[]}
   */
  public errorHandler(
    errorMessages: ErrorMessages[],
    paymentDetails: PaymentInformation
  ): string[] {
    const uiMessage: string[] = [];
    // let model = paymentDetails;
    errorMessages.map((errorMessage: ErrorMessages) => {
      uiMessage.push(errorMessage.uiMessage);
      this.handlePaymentErrors(errorMessage.uiShortMessageCode, paymentDetails);
    });
    return uiMessage;
  }

  /**
   * Generates paymentID
   */
  public getPaymentId(): number {
    return randomNumber() * new Date().valueOf();
  }

  /**
   * Used to
   * @param {string} uiShortErrorMessage
   * @param {PaymentCardModel} paymentDetails
   */
  /*tslint:disable:cyclomatic-complexity*/
  private handlePaymentErrors(
    uiShortErrorMessage: string,
    paymentDetails: PaymentInformation
  ): PaymentInformation {
    // let errorCodeDetails = '';
    // switch (uiShortErrorMessage.toLocaleUpperCase()) {
    //   case '26':
    //     paymentDetails.cvv = '';
    //     break;
    //   case '27':
    //   case 'REVENUE-008':
    //     paymentDetails.initData();
    //     break;
    //   case 'REVENUE-002':
    //   case 'REVENUE-004':
    //     paymentDetails.fullName = '';
    //     break;
    //   case '24':
    //     paymentDetails.zipCode = '';
    //     break;
    //   case '8':
    //   case '9':
    //     paymentDetails.initData();
    //     break;
    //   case '10':
    //     paymentDetails.month = '';
    //     paymentDetails.year = '';
    //     break;
    //   case 'REVENUE-005':
    //   case 'REVENUE-007':
    //   case 'REVENUE-072':
    //   case 'REVENUE-073':
    //     paymentDetails.cardNumber = '';
    //     break;
    //   case '140':
    //     paymentDetails.initData();
    //     errorCodeDetails = 'CCError';
    //     break;
    //   case '141':
    //     paymentDetails.initData();
    //     errorCodeDetails = 'BANError';
    //     break;
    //   default:
    //     paymentDetails.initData();
    //     break;
    // }
    // this.negativeFileCheck.emit(errorCodeDetails);
    return paymentDetails;
  }

  public setVestaToken(token: string): void {
    this.vestaToken = token;
  }

  public getVestaToken(): string {
    return this.vestaToken;
  }

  /**
   * Retrives the Card Info from the API 2611
   * @returns Observable<CardTypeValidation>
   */
  public cardTypeValidation(cardNumber: string): Observable<CardTypeValidation> {
    const payload = {
      bin: cardNumber,
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2611_cardTypeValidation, payload, null, null, null)
      .pipe(
        map((response: HttpResponse<CardTypeValidation>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }
}
